// Inspired by react-hot-toast library
"use client";

import { Toast as ToastPrimitive } from "@base-ui/react/toast";
import type { ReactNode } from "react";

import type {
  ToastActionElement,
  ToastProps,
  ToastVariant,
} from "@/components/ui/toast";
import { buildToastCopyText } from "@/components/ui/toast-helpers";

const DEFAULT_ERROR_TOAST_DURATION = 300000; // 5 minutes

type ToasterToast = {
  id?: string;
  title?: ReactNode;
  description?: ReactNode;
  details?: ReactNode;
  duration?: number;
  variant?: ToastVariant;
  action?: ToastActionElement;
  icon?: ReactNode;
};

type Toast = Omit<ToasterToast, "id">;

type ToastData = {
  action?: ToastActionElement;
  copyText?: string;
  details?: ReactNode;
  icon?: ReactNode;
};

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}

export const toastManager = ToastPrimitive.createToastManager<ToastData>();

function toToastManagerOptions(props: Toast | ToasterToast) {
  return {
    title: props.title,
    description: props.description,
    timeout:
      props.duration ??
      (props.variant === "destructive"
        ? DEFAULT_ERROR_TOAST_DURATION
        : undefined),
    type: props.variant,
    data:
      props.action ||
      props.details ||
      props.icon ||
      (props.variant === "destructive" &&
        buildToastCopyText(props).trim().length > 0)
        ? {
            ...(props.action ? { action: props.action } : {}),
            ...(props.details ? { details: props.details } : {}),
            ...(props.icon ? { icon: props.icon } : {}),
            ...(props.variant === "destructive"
              ? { copyText: buildToastCopyText(props) }
              : {}),
          }
        : undefined,
  };
}

export function toast(props: ToastProps) {
  const skip =
    process.env.NODE_ENV === "production" &&
    typeof props.description === "string" &&
    props.description.includes("Server Components render");

  const id = genId();

  const update = (nextProps: ToasterToast) =>
    toastManager.update(id, toToastManagerOptions(nextProps));
  const dismiss = () => toastManager.close(id);

  if (!skip) {
    toastManager.add({
      id,
      ...toToastManagerOptions(props),
    });
  }

  return {
    id,
    dismiss,
    update,
  };
}

export function useToast() {
  return {
    toasts: [] as never[],
    toast,
    dismiss: (toastId?: string) => toastManager.close(toastId),
  };
}
